import { useCallback, useEffect, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Avatar, Badge, Button } from '@mantine/core'
import DashboardWrapper from '../../components/MainDashboardWrapper/DashboardWrapper'
import { approveCoachCredential, getCoachCredentials, getUserAvailability, getUserDetail } from './helper'
import UserDayCalendar from './UserDayCalendar'
import SlotMatchChecker from './SlotMatchChecker'
import AdminAccessCard from './AdminAccessCard'

interface IProps extends RouteComponentProps<{ id: string }> {}

const statusColor: Record<string, string> = {
  approved: 'green',
  pending: 'yellow',
  rejected: 'red',
}

const weekdayChipClasses = ['bg-[#FEF3C7] text-[#92400E]', 'bg-[#E0F2FE] text-[#075985]']

const getInitials = (name?: string) => {
  if (!name) return ''
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')
}

const UserDetailPage: React.FC<IProps> = ({ match }) => {
  const [user, setUser] = useState<any>()
  const [credentials, setCredentials] = useState<any[]>([])
  const [availability, setAvailability] = useState<any>()

  const loadUser = useCallback(() => {
    getUserDetail(match.params.id).then((res) => setUser(res.data))
  }, [match.params.id])

  useEffect(() => {
    loadUser()
  }, [loadUser])

  useEffect(() => {
    if (user) {
      getUserAvailability(match.params.id, !!user.isProvider).then((res) => setAvailability(res.data))
    }
  }, [user, match.params.id])

  const loadCredentials = useCallback(() => {
    getCoachCredentials(match.params.id).then((res) => setCredentials(res.data || []))
  }, [match.params.id])

  useEffect(() => {
    if (user?.isProvider) loadCredentials()
  }, [user?.isProvider, loadCredentials])

  const onApprove = (credentialId: string) => {
    approveCoachCredential(match.params.id, credentialId)
      .then(() => loadCredentials())
      .catch((err) => {
        alert(err.response?.data?.message || 'Failed to approve credential')
      })
  }

  const timezoneName = availability?.time?.timeZone?.zoneName || availability?.timezone
  const utcOffset = availability?.time?.timeZone?.utcOffset

  return (
    <DashboardWrapper page="all-users" title={user?.displayName || 'User'}>
      <div className="flex items-center gap-5 bg-white rounded-xl shadow-md p-6">
        <Avatar
          src={user?.profilePic}
          size={80}
          radius={80}
          styles={{ placeholder: { backgroundColor: '#0B1D3A', color: '#fff', fontWeight: 700 } }}
        >
          {getInitials(user?.displayName)}
        </Avatar>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold">{user?.displayName}</p>
            <Badge color="cta">{user?.isProvider ? 'Coach' : 'Client'}</Badge>
          </div>
          <p className="text-gray-600">{user?.email}</p>
          {user?.phoneNumber && <p className="text-gray-600">{user.phoneNumber}</p>}
          {user?.location && <p className="text-gray-600">{user.location}</p>}
          {user?.bio && <p className="mt-2">{user.bio}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6 mt-6">
        <p className="text-xl font-bold mb-4">Availability</p>

        {!timezoneName && !availability?.weekdays?.length && (
          <p className="text-gray-500">No availability set</p>
        )}

        {timezoneName && (
          <p className="text-gray-600 mb-4">
            Timezone: <span className="font-bold">{timezoneName}</span>
            {utcOffset ? ` (UTC${utcOffset})` : ''}
          </p>
        )}

        {availability?.weekdays?.length > 0 && (
          <div className="flex flex-col gap-3">
            {availability.weekdays.map((wd: any, idx: number) => (
              <div key={idx} className="flex items-center gap-4">
                <p className="font-bold w-24">{wd.day}</p>
                <span className={`px-3 py-1 rounded-md text-sm ${weekdayChipClasses[idx === 0 ? 0 : 1]}`}>
                  {wd.from} – {wd.to}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {user && (
        <AdminAccessCard
          userId={match.params.id}
          name={user.displayName}
          isSuperAdmin={!!user.isSuperAdmin}
          isReportViewer={!!user.isReportViewer}
          onChanged={loadUser}
        />
      )}

      {user && <UserDayCalendar userId={match.params.id} isProvider={!!user.isProvider} />}

      {user && !user.isProvider && <SlotMatchChecker clientId={match.params.id} clientName={user.displayName} />}

      {user?.isProvider && (
        <div className="bg-white rounded-xl shadow-md p-6 mt-6">
          <p className="text-xl font-bold mb-4">Certificates</p>

          {credentials.length === 0 && <p className="text-gray-500">No certificates submitted</p>}

          <div className="flex flex-col gap-3">
            {credentials.map((cred) => (
              <div key={cred.id} className="flex items-center justify-between border-b pb-3">
                <div>
                  <p className="font-bold">{cred.title}</p>
                  <p className="text-gray-600 text-sm">
                    {cred.issuingOrg}
                    {cred.issuedYear ? ` · ${cred.issuedYear}` : ''}
                  </p>
                  {cred.fileUrl && (
                    <a
                      href={cred.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-sm text-blue-600 underline"
                    >
                      View file
                    </a>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <Badge color={statusColor[cred.status] || 'gray'}>{cred.status}</Badge>
                  {cred.status === 'pending' && (
                    <Button color="green" onClick={() => onApprove(cred.id)}>
                      Approve
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </DashboardWrapper>
  )
}

export default UserDetailPage
